import { Button } from '@lokalise/louis'
import type {
  AiConfigLanguage,
  AiConfigProject,
  Integrations,
} from '@lokalise/translation-api-schemas'
import type { AiConfigProjectWithLanguages } from '../../../../types/projects'
import { getOptionWarning, getTruncatedOptionsWarning } from '../../../../utils/warnings'
import {
  KeysForLanguageBorderWrapper,
  KeysForLanguageContainer,
  KeysForLanguageContents,
  KeysForLanguageHeader,
  KeysForLanguagesProjectDetails,
  KeysForLanguagesSourceArrow,
  KeysForLanguagesSourceCard,
  KeysForLanguagesTargetLanguagesCard,
  KeysForLanguagesTargetLanguagesContainer,
} from '../../../KeysForLanguage/KeysForLanguage'
import { Select } from '../../../Select/Select'
import {
  StepContainer,
  StepContents,
  StepFooter,
  StepRSP,
  StepSubtitle,
  StepTitle,
  StepTitleContainer,
} from '../../../Step/Step'

type ActivationStepProps = {
  projects: AiConfigProjectWithLanguages[]
  selectedProjects: AiConfigProjectWithLanguages[]
  isActivating: boolean
  isEdit: boolean
  name: string
  targetLangs: AiConfigLanguage[]
  sourceLang: AiConfigLanguage | undefined
  integration: Integrations
  onProjectsChange: (projects: AiConfigProject[]) => void
  onClose: () => void
  onActivate: () => void
}

export const ActivationStep = ({
  projects,
  selectedProjects,
  isActivating,
  isEdit,
  name,
  targetLangs,
  sourceLang,
  integration,
  onProjectsChange,
  onActivate,
  onClose,
}: ActivationStepProps) => {
  return (
    <StepContainer>
      <StepContents>
        <StepTitleContainer>
          <StepTitle>{isEdit ? 'Update' : 'Activate'} AI profile</StepTitle>
          <StepSubtitle>
            Once activated, all translations in the selected projects will be managed by the AI
            profile for your chosen languages.
          </StepSubtitle>
        </StepTitleContainer>
        <Select
          title="Choose projects to activate the AI profile"
          name="projects"
          placeholder="Choose"
          options={projects}
          selectedOptions={selectedProjects}
          onSelectionChange={(selection) => onProjectsChange([...selection])}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          getOptionWarning={(option) =>
            getOptionWarning(
              `${option.baseLangId}`,
              option.targetLanguages,
              sourceLang,
              targetLangs,
            )
          }
          maxVisibleOptions={10}
          getTruncatedOptionsWarning={(options) =>
            getTruncatedOptionsWarning(sourceLang, options, targetLangs)
          }
        />
        <StepFooter>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onActivate}
            loading={isActivating}
            disabled={isActivating}
            variant="primary"
          >
            {isEdit ? 'Update' : 'Activate'}
          </Button>
        </StepFooter>
      </StepContents>
      <StepRSP>
        <KeysForLanguageContainer>
          <KeysForLanguageBorderWrapper>
            <KeysForLanguageHeader
              name={name || 'Select your AI profile name'}
              integration={integration}
            />
            <KeysForLanguageContents>
              <KeysForLanguagesSourceCard sourceLanguage={sourceLang} />

              <KeysForLanguagesSourceArrow />

              <KeysForLanguagesTargetLanguagesContainer>
                <KeysForLanguagesTargetLanguagesCard numberOfTargetLanguages={targetLangs.length} />
              </KeysForLanguagesTargetLanguagesContainer>

              <KeysForLanguagesProjectDetails
                highlighted
                projects={selectedProjects}
                hasProjectWarning={(project) =>
                  !!getOptionWarning(
                    `${project.baseLangId}`,
                    project.targetLanguages,
                    sourceLang,
                    targetLangs,
                  )
                }
              />
            </KeysForLanguageContents>
          </KeysForLanguageBorderWrapper>
        </KeysForLanguageContainer>
      </StepRSP>
    </StepContainer>
  )
}
